import { ApiError, type Api, type Filter } from "../../../data/api";
import {
  ueberschreibungsZeile, type Antwort, type AntwortRow, type Benachrichtigung, type BrandLite, type DealLite, type Eintrag, type Farbe, type FeedRow, type GruppeRow,
  type InvoiceLite, type MitarbeiterLite, type MitgliedRow, type PeriodLite, type Perspektive, type TalentLite, type TerminDaten, type TerminRow,
  type BeitragLite,
} from "./model";
import { t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege des Kalenders. Sie bekommen den Datenanschluss übergeben, damit dieselben Funktionen in der App
 * (Supabase oder Demo) und in den Tests gegen die Demo-Datenbank laufen. Rechte prüft immer die Datenbank (RLS, Wächter
 * app.guard_calendar_event); wo RLS eine Änderung still ins Leere laufen lässt, melden die Schreibwege das ausdrücklich.
 */

export interface KalenderDaten {
  feed: FeedRow[];
  termine: TerminRow[];
  talents: TalentLite[];
  gruppen: GruppeRow[];
  mitglieder: MitgliedRow[];
  deals: DealLite[];
  brands: BrandLite[];
  invoices: InvoiceLite[];
  perioden: PeriodLite[];
  mitarbeiter: MitarbeiterLite[];
  /** Feiertagsregion (Creator: eigene Wahl, sonst die der Agentur) und Zeitzone der Agentur (org_profiles, auch für Creator lesbar) */
  region: string | null;
  /** Region der Agentur (für die Auswahl „wie die Agentur“) */
  agenturRegion: string | null;
  /** Creator: eigene Feiertagsregion (talents.holiday_region, 0041), null = wie die Agentur */
  eigeneRegion: string | null;
  zeitzone: string | null;
  /** Zusagen, Absagen und Rückfragen der Creator zu Terminen (0056; Creator sehen nur die eigenen) */
  antworten: AntwortRow[];
  /** Beiträge (0068) für Entwurfsfrist und Veröffentlichung: Bezeichnung und Stand */
  beitraege: BeitragLite[];
}

export const TERMIN_SPALTEN =
  "id, org_id, title, description, starts_on, ends_on, start_time, end_time, time_zone, location, meeting_url, copy_group, color, audience, group_id, talent_id, source_key, hidden, " +
  "created_by, created_by_side, created_at, updated_at";

/** Nicht lesbar (RLS) ergibt eine leere Liste: Gruppen und Team sieht nur die Agentur. */
function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch(() => [] as T[]);
}

/**
 * Alles für einen Kalenderausschnitt. Der Feed (calendar_feed, als Lesefunktion über api.read) liefert die Termine aller
 * Agenturen der Person mit ihrer Agentur (0054) und wird hier auf die gewählte Agentur begrenzt, wie die übrigen Abfragen.
 */
export async function ladeKalender(api: Api, a: { orgId: string; perspektive: Perspektive; von: string; bis: string; talentId?: string | null }): Promise<KalenderDaten> {
  const byOrg: Filter[] = [["org_id", "eq", a.orgId]];
  const agentur = a.perspektive === "agentur";
  const [feed, termine, talents, gruppen, mitglieder, deals, brands, invoices, perioden, mitarbeiter, profil, eigen, antworten, beitraege] = await Promise.all([
    // p_org (0203, Praxistest G-134): die Datenbank begrenzt schon selbst auf die Agentur, statt alle Agenturen zu liefern
    api.read<FeedRow[] | null>("calendar_feed", { p_from: a.von, p_to: a.bis, p_org: a.orgId }).then((r) => (r ?? []).filter((x) => !x.org_id || x.org_id === a.orgId)),
    api.select<TerminRow>("calendar_events", { columns: TERMIN_SPALTEN, filters: byOrg, order: [["starts_on", "asc"]] }),
    quiet(api.select<TalentLite>("talents", { columns: "id, first_name, last_name, display_name, nickname, status", filters: byOrg, order: [["first_name", "asc"]] })),
    agentur ? quiet(api.select<GruppeRow>("talent_groups", { columns: "id, name", filters: byOrg, order: [["name", "asc"]] })) : Promise.resolve([]),
    agentur ? quiet(api.select<MitgliedRow>("talent_group_members", { columns: "group_id, talent_id", filters: byOrg })) : Promise.resolve([]),
    // Creator lesen Sichten ohne interne Spalten (0037)
    quiet(api.select<DealLite>(agentur ? "deals" : "talent_deals", { columns: "id, title, brand_id, talent_id, status", filters: byOrg })),
    quiet(api.select<BrandLite>(agentur ? "brands" : "talent_brands", { columns: "id, name", filters: byOrg })),
    quiet(api.select<InvoiceLite>("invoices", { columns: "id, number, kind, brand_id, deal_id, talent_id, gross_cents, currency", filters: byOrg })),
    quiet(api.select<PeriodLite>(agentur ? "deal_periods" : "talent_deal_periods", { columns: "id, deal_id, month", filters: byOrg })),
    agentur ? quiet(api.select<MitarbeiterLite>("memberships", { columns: "user_id, display_name", filters: byOrg })) : Promise.resolve([]),
    quiet(api.select<{ holiday_region: string | null; timezone: string | null }>("org_profiles", { columns: "holiday_region, timezone", filters: byOrg })),
    !agentur && a.talentId
      ? quiet(api.select<{ holiday_region: string | null }>("talents", { columns: "holiday_region", filters: [...byOrg, ["id", "eq", a.talentId]] }))
      : Promise.resolve([] as { holiday_region: string | null }[]),
    quiet(api.select<AntwortRow>("calendar_event_responses", { columns: "event_id, talent_id, response, note, responded_at", filters: byOrg })),
    // Creator lesen die Sicht ohne interne Angaben (0068)
    quiet(api.select<BeitragLite>(agentur ? "deal_deliverables" : "talent_deal_deliverables", {
      columns: "id, deal_id, period_id, platform, format, format_label, title, status", filters: byOrg,
    })),
  ]);
  const agenturRegion = profil[0]?.holiday_region ?? null;
  const eigeneRegion = eigen[0]?.holiday_region ?? null;
  return {
    feed, termine, talents, gruppen, mitglieder, deals, brands, invoices, perioden, mitarbeiter,
    region: eigeneRegion ?? agenturRegion, agenturRegion, eigeneRegion, zeitzone: profil[0]?.timezone ?? null, antworten, beitraege,
  };
}

/** Termin für einen Sprunglink (Glocke, Mail: ?termin=<id>): nur Datum, damit der Kalender den Monat öffnen kann. */
export async function ladeTerminDatum(api: Api, orgId: string, id: string): Promise<string | null> {
  if (!/^[0-9a-f-]{36}$/i.test(id)) return null;
  const rows = await quiet(api.select<{ starts_on: string }>("calendar_events", { columns: "starts_on", filters: [["id", "eq", id], ["org_id", "eq", orgId]] }));
  return rows[0]?.starts_on ?? null;
}

// ── Schreibwege ────────────────────────────────────────────────────────────

function verweigert(was: string): ApiError {
  return new ApiError(t(`${was} nicht möglich: Der Eintrag wurde nicht gefunden oder ist für dein Konto nicht freigegeben.`, `Cannot ${was}: the entry was not found or is not available to your account.`), { code: "42501" });
}

/** Neuer Termin; Seite und Person setzt die Datenbank (Talents nur eigene Termine). */
export async function legeTerminAn(api: Api, orgId: string, daten: TerminDaten): Promise<TerminRow> {
  const rows = await api.insert<TerminRow>("calendar_events", { org_id: orgId, ...daten });
  if (!rows[0]) throw verweigert(t("Anlegen", "create"));
  return rows[0];
}

export async function aendereTermin(api: Api, id: string, daten: TerminDaten): Promise<TerminRow> {
  const rows = await api.update<TerminRow>("calendar_events", [["id", "eq", id]], { ...daten });
  if (!rows[0]) throw verweigert(t("Ändern", "change"));
  return rows[0];
}

export async function loescheTermin(api: Api, id: string, copyGroup: string | null = null): Promise<void> {
  // K-527: ein Termin für mehrere Creator wird gemeinsam gelöscht (jeder benachrichtigte Creator bekommt seine Absage)
  const filter = copyGroup ? [["copy_group", "eq", copyGroup] as const] : [["id", "eq", id] as const];
  await api.remove("calendar_events", filter);
  const rest = await api.select<{ id: string }>("calendar_events", { columns: "id", filters: filter });
  if (rest.length) throw verweigert(t("Löschen", "delete"));
}

/**
 * K-527 (0395): Termin für einen oder mehrere Creator speichern. Je Creator eine Zeile (Zielgruppe „talent“) mit gemeinsamer
 * copy_group; bestehende Zeilen werden geändert, neue Creator bekommen eine Zeile, abgewählte verlieren ihre (mit Absage wie beim
 * Löschen). Benachrichtigt wird je Zeile; der Bericht fasst alle zusammen.
 */
export async function speichereTermineFuerCreator(api: Api, a: {
  orgId: string; termin: TerminRow | null; geschwister: readonly TerminRow[]; daten: TerminDaten; talentIds: readonly string[]; benachrichtigen: boolean; perMail: boolean;
}): Promise<{ termin: TerminRow; benachrichtigt: Benachrichtigung | null; hinweis: string | null }> {
  const bisher = a.termin ? (a.termin.copy_group ? a.geschwister.filter((x) => x.copy_group === a.termin!.copy_group) : [a.termin]) : [];
  const gruppe = a.talentIds.length > 1 || bisher.length > 1 ? (a.termin?.copy_group ?? crypto.randomUUID()) : (a.termin?.copy_group ?? null);
  const gespeichert: { row: TerminRow; neu: boolean }[] = [];
  for (const tid of a.talentIds) {
    const alt = bisher.find((x) => x.talent_id === tid);
    if (alt) {
      const rows = await api.update<TerminRow>("calendar_events", [["id", "eq", alt.id]], { ...a.daten, talent_id: tid, ...(gruppe !== (alt.copy_group ?? null) ? { copy_group: gruppe } : {}) });
      if (!rows[0]) throw verweigert(t("Ändern", "change"));
      gespeichert.push({ row: rows[0], neu: false });
    } else {
      const rows = await api.insert<TerminRow>("calendar_events", { org_id: a.orgId, ...a.daten, talent_id: tid, copy_group: gruppe });
      if (!rows[0]) throw verweigert(t("Anlegen", "create"));
      gespeichert.push({ row: rows[0], neu: true });
    }
  }
  for (const weg of bisher.filter((x) => !a.talentIds.includes(x.talent_id ?? ""))) await api.remove("calendar_events", [["id", "eq", weg.id]]);
  const termin = gespeichert.find((g) => g.row.id === a.termin?.id)?.row ?? gespeichert[0]!.row;
  if (!a.benachrichtigen) return { termin, benachrichtigt: null, hinweis: null };
  const summe: Benachrichtigung = { talents: 0, mails: 0, no_address: [], opted_out: 0, perMail: a.perMail };
  try {
    for (const g of gespeichert) {
      const b = await benachrichtige(api, g.row.id, a.perMail, !g.neu);
      summe.talents += b.talents; summe.mails += b.mails; summe.no_address.push(...b.no_address); summe.opted_out += b.opted_out;
    }
    return { termin, benachrichtigt: summe, hinweis: null };
  } catch (e) {
    return { termin, benachrichtigt: null, hinweis: t(`Termin gespeichert, aber die Benachrichtigung ist fehlgeschlagen: ${(e as Error).message}`, `Appointment saved, but the notification failed: ${(e as Error).message}`) };
  }
}

/**
 * Creator über einen Termin informieren (AD-189): Glocke immer, Mail auf Wunsch. Liefert den Bericht der Datenbank
 * (0041): wie viele Creator, wie viele Mails eingereiht, wer keine Adresse hat, wer Info-Mails abbestellt hat.
 */
export async function benachrichtige(api: Api, id: string, perMail: boolean, geaendert = false, nurOffene = false): Promise<Benachrichtigung> {
  const r = await api.rpc<Partial<Benachrichtigung> | null>("calendar_event_notify_report", {
    p_id: id, p_email: perMail, p_changed: geaendert, ...(nurOffene ? { p_only_open: true } : {}),
  });
  return {
    talents: Number(r?.talents ?? 0), mails: Number(r?.mails ?? 0), no_address: r?.no_address ?? [], opted_out: Number(r?.opted_out ?? 0), perMail,
    ...(r?.already_answered !== undefined ? { already_answered: Number(r.already_answered) } : {}),
  };
}

/** Creator: zusagen, absagen oder nachfragen (0056). Die Rückfrage landet zusätzlich im Chat. */
export async function antworte(api: Api, eventId: string, antwort: Antwort, notiz: string | null): Promise<true> {
  await api.rpc("calendar_event_respond", { p_event: eventId, p_response: antwort, ...(notiz?.trim() ? { p_note: notiz.trim() } : {}) });
  return true;
}

/** Kalender-Abo: neuen geheimen Link erzeugen (ein älterer wird ungültig) bzw. das Abo beenden. */
export async function aboErzeugen(api: Api, orgId: string): Promise<string> {
  return api.rpc<string>("calendar_subscription_create", { p_org: orgId });
}
export async function aboBeenden(api: Api, orgId: string): Promise<void> {
  await api.rpc("calendar_subscription_revoke", { p_org: orgId });
}
export async function aboStand(api: Api, userId: string, orgId: string): Promise<{ created_at: string; last_used_at: string | null } | null> {
  const rows = await quiet(api.select<{ created_at: string; last_used_at: string | null }>("calendar_subscriptions", {
    columns: "created_at, last_used_at", filters: [["user_id", "eq", userId], ["org_id", "eq", orgId]],
  }));
  return rows[0] ?? null;
}

/** Creator: eigene Feiertagsregion (null = wie die Agentur). */
export async function speichereFeiertagsregion(api: Api, talentId: string, region: string | null): Promise<void> {
  await api.rpc("talent_set_preferences", { p_talent: talentId, p_holiday_region: region, p_clear_region: !region });
}

/**
 * Speichern aus dem Formular: anlegen oder ändern, danach auf Wunsch benachrichtigen. Scheitert nur die Benachrichtigung,
 * bleibt der Termin gespeichert und der Hinweis sagt es.
 */
export async function speichereTermin(api: Api, a: { orgId: string; id: string | null; daten: TerminDaten; benachrichtigen: boolean; perMail: boolean }): Promise<{
  termin: TerminRow; benachrichtigt: Benachrichtigung | null; hinweis: string | null;
}> {
  const termin = a.id ? await aendereTermin(api, a.id, a.daten) : await legeTerminAn(api, a.orgId, a.daten);
  if (!a.benachrichtigen || a.daten.audience === "staff") return { termin, benachrichtigt: null, hinweis: null };
  try {
    // Bestehender Termin: „Termin geändert“ mit dem bisherigen Datum, nicht „Neuer Termin“ (Befund 6)
    return { termin, benachrichtigt: await benachrichtige(api, termin.id, a.perMail, !!a.id), hinweis: null };
  } catch (e) {
    return { termin, benachrichtigt: null, hinweis: t(`Termin gespeichert, aber die Benachrichtigung ist fehlgeschlagen: ${(e as Error).message}`, `Appointment saved, but the notification failed: ${(e as Error).message}`) };
  }
}

/** Automatischen Termin überschreiben (Titel, Datum, Farbe) oder aus- und wieder einblenden (AD-190). Nur das Team. */
export async function speichereUeberschreibung(api: Api, a: {
  orgId: string; eintrag: Eintrag; zone: string; daten: { title: string; starts_on: string; color: Farbe; hidden?: boolean };
}): Promise<TerminRow> {
  const o = a.eintrag.ueberschreibung;
  if (o) {
    const rows = await api.update<TerminRow>("calendar_events", [["id", "eq", o.id]], {
      title: a.daten.title, starts_on: a.daten.starts_on, color: a.daten.color, hidden: a.daten.hidden ?? false,
    });
    if (!rows[0]) throw verweigert(t("Ändern", "change"));
    return rows[0];
  }
  const rows = await api.insert<TerminRow>("calendar_events", ueberschreibungsZeile(a.eintrag, a.orgId, a.zone, a.daten));
  if (!rows[0]) throw verweigert(t("Überschreiben", "overwrite"));
  return rows[0];
}

/** Überschreibung entfernen: der automatische Termin erscheint wieder mit den Werten aus Rechnung oder Kooperation. */
export async function setzeUeberschreibungZurueck(api: Api, id: string): Promise<void> {
  await loescheTermin(api, id);
}

// ── Gruppen (Zielgruppe „Gruppe“) ──────────────────────────────────────────

function gruppenFehler(e: unknown): never {
  const err = e as { code?: string; message?: string };
  if (err?.code === "23505" || /duplicate key|unique/i.test(err?.message ?? "")) {
    throw new ApiError(t("Eine Gruppe mit diesem Namen gibt es schon. Bitte einen anderen Namen wählen.", "A group with this name already exists. Please choose another name."), { code: "23505" });
  }
  throw e;
}

/**
 * Gruppe anlegen oder ändern. Mitglieder werden nur für Creator geändert, die die Person sehen darf (`sichtbar`):
 * eingeschränkte Manager lassen fremde Mitglieder unberührt (RLS würde sie ohnehin nicht ändern).
 */
export async function speichereGruppe(api: Api, a: {
  orgId: string; id: string | null; name: string; talentIds: readonly string[]; bisher: readonly string[]; sichtbar: ReadonlySet<string>;
}): Promise<string> {
  let gid = a.id;
  try {
    if (gid) {
      const rows = await api.update<GruppeRow>("talent_groups", [["id", "eq", gid]], { name: a.name });
      if (!rows[0]) throw verweigert(t("Ändern", "change"));
    } else {
      const rows = await api.insert<GruppeRow>("talent_groups", { org_id: a.orgId, name: a.name });
      if (!rows[0]) throw verweigert(t("Anlegen", "create"));
      gid = rows[0].id;
    }
  } catch (e) {
    gruppenFehler(e);
  }
  const add = a.talentIds.filter((t) => !a.bisher.includes(t));
  const del = a.bisher.filter((t) => a.sichtbar.has(t) && !a.talentIds.includes(t));
  if (add.length) await api.insert("talent_group_members", add.map((t) => ({ group_id: gid, talent_id: t, org_id: a.orgId })));
  for (const t of del) await api.remove("talent_group_members", [["group_id", "eq", gid!], ["talent_id", "eq", t]]);
  return gid!;
}

/** Gruppe löschen: ihre Termine löscht die Datenbank mit (Fremdschlüssel mit Kaskade). */
export async function loescheGruppe(api: Api, id: string): Promise<void> {
  await api.remove("talent_groups", [["id", "eq", id]]);
  const rest = await api.select<{ id: string }>("talent_groups", { columns: "id", filters: [["id", "eq", id]] });
  if (rest.length) throw verweigert(t("Löschen", "delete"));
}

